/* ───────────────────────────────────────────────────────────────────────────
   O16 — mounts Contribe's cause picker inside our cart pop-up.

   WHY THIS EXISTS
   Contribe's plugin decides where to render itself from a server side field,
   loaderScriptConfig, which is returned by
   plugins.contribe.io/api/plugins/v1/<shop>/config. For this shop that field
   is an empty list, so their script has no instruction to fill anything and
   our pop-up stayed blank — even though the same config carries a live,
   complete setup: three causes with images and copy, and disabled: false.

   Rather than wait for that field, we mount their own <contribe-plugin>
   component ourselves. We own the placement; they keep the donation. Verified
   on DEV 2026-09-09: all three causes render, selecting one sets
   selectedProject and POSTs to their /interaction endpoint, so the choice
   registers with them exactly as it would if they had placed it.

   THE CART CONTEXT MATTERS
   cartid, cartsize and cartvalue are how a donation is tied to the order. We
   re-read them from /cart.js every time the pop-up opens rather than once at
   load, because the cart changes underneath a page that is never reloaded in
   this theme.
─────────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var HOST = '[data-o16-cause-content]';
  var PLUGIN = 'contribe-plugin';

  /** Their own badge carries the ids they were configured with; prefer those
      over anything we hard code, so a change on their side follows through. */
  function pluginId() {
    var badge = document.querySelector('contribe-badge');
    var fromBadge = badge && badge.getAttribute('pluginid');
    if (fromBadge) return fromBadge;
    if (window.Shopify && window.Shopify.shop) return window.Shopify.shop;
    return null;
  }

  function market() {
    var badge = document.querySelector('contribe-badge');
    var m = badge && badge.getAttribute('market');
    if (m) return m;
    var loc = (window.Shopify && window.Shopify.country) || 'DK';
    return String(loc).toLowerCase();
  }

  function cart() {
    var base = (window.routes && window.routes.cart_url) || '/cart';
    return fetch(base + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .catch(function () { return null; });
  }

  /** Create it once, then only refresh the cart attributes on later opens. */
  function mount() {
    var host = document.querySelector(HOST);
    if (!host) return;
    var id = pluginId();
    // Without an id their component cannot ask for its own config, and an
    // element that renders nothing is worse than an empty panel: it looks
    // broken rather than unfinished.
    if (!id) return;

    cart().then(function (c) {
      if (!c) return;
      var el = host.querySelector(PLUGIN);
      if (!el) {
        el = document.createElement(PLUGIN);
        el.setAttribute('pluginid', id);
        el.setAttribute('market', market());
        host.appendChild(el);
      }
      el.setAttribute('cartid', c.token || '');
      el.setAttribute('cartsize', String(c.item_count || 0));
      el.setAttribute('cartvalue', String(c.total_price || 0));
      // A shopper who already chose earlier in the session comes back to a
      // panel that should show Apply, not the introduction.
      setTimeout(reflect, 900);
    });
  }

  /* ── OUR HALF OF THE PANEL ───────────────────────────────────────────────
     Their component grows by a description once a cause is chosen. Before
     that the space stood empty, so we hold the idea there and step aside the
     moment their description arrives. And we show an Apply button, because
     the choice is already registered on click and the only other way out is
     the close cross, which reads as cancelling something the shopper just
     completed.

     We never reach into their shadow root. Selection is read from the public
     selectedProject property after a click, which is composed and therefore
     crosses the shadow boundary on its own. */
  function chosen() {
    var el = document.querySelector(HOST + ' ' + PLUGIN);
    return !!(el && el.selectedProject);
  }

  function reflect() {
    var picked = chosen();
    var intro = document.querySelector('[data-o16-cause-intro]');
    var apply = document.querySelector('[data-o16-cause-apply]');
    // Collapse rather than hide: `hidden` removes the element in one frame
    // and the panel snaps. The class animates max-height and opacity, so the
    // space is handed over instead of dropped.
    if (intro) intro.classList.toggle('is-collapsed', picked);
    // The button is always on screen; only its state changes. Appearing and
    // disappearing was itself one of the movements the panel was asked to
    // stop making.
    if (apply) apply.setAttribute('aria-disabled', picked ? 'false' : 'true');
  }

  document.addEventListener('click', function (e) {
    var host = document.querySelector(HOST);
    if (!host || !e.target) return;
    if (!(host.contains(e.target) || (e.composedPath && e.composedPath().indexOf(host) !== -1))) return;
    // ONE movement, not two. Waiting for selectedProject before collapsing
    // the introduction meant their description appeared first, the panel
    // grew, and only then did the introduction vanish and the panel shrink
    // back — a visible bounce. Collapse it on the click instead, so the
    // panel makes a single move as their description takes the space.
    var intro = document.querySelector('[data-o16-cause-intro]');
    if (intro) intro.classList.add('is-collapsed');
    setTimeout(reflect, 120);
  }, true);

  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-o16-cause-apply]');
    if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
    // Confirm and close. Nothing to submit: their /interaction call already
    // went out when the cause was picked.
    var modal = document.querySelector('o16-cause-modal');
    var close = modal && modal.querySelector('[data-o16-cause-close]');
    if (close) close.click();
    else if (modal) modal.hidden = true;
  });

  // Mount when the pop-up is actually opened. Their component fetches its own
  // config and icons on connect, so building it on every page load would cost
  // three requests for a panel most shoppers never open.
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest && e.target.closest('[data-o16-cause-trigger]');
    if (!t) return;
    // After the modal's own handler has run, so the panel has layout.
    setTimeout(mount, 60);
  }, true);

  // The drawer re-renders on cart changes and can replace the pop-up's
  // content node. Re-mounting on open covers that, but if the panel is open
  // at the time, refresh it directly.
  document.addEventListener('o16:cart:updated', function () {
    var m = document.querySelector('o16-cause-modal');
    if (m && !m.hasAttribute('hidden')) mount();
  });
})();
